import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type User } from '@/api/client';
import { MODULES, TopBar } from '@/components/Chrome';
import { Palette } from '@/components/Palette';
import { PasswordDialog } from '@/components/PasswordDialog';
import { Shortcuts } from '@/components/Shortcuts';
import { ENV_ORDER, envStyle } from '@/lib/format';
import { navigate, usePath } from '@/lib/router';
import { clearGo, type Command, goPending, markGo, type Shell, ShellContext } from '@/lib/shell';
import { Alerts } from '@/pages/Alerts';
import { Boards } from '@/pages/Boards';
import { Explore } from '@/pages/Explore';
import { IssuePage } from '@/pages/Issue';
import { Issues } from '@/pages/Issues';
import { Placeholder } from '@/pages/Placeholder';
import { Sources } from '@/pages/Sources';
import { Team } from '@/pages/Team';

const ENV_KEY = 'fl.env';
const rank = (env: string): number => {
    const index = ENV_ORDER.indexOf(env);

    return index < 0 ? ENV_ORDER.length : index;
};

function storedEnv(): string | null {
    try {
        return localStorage.getItem(ENV_KEY);
    } catch {
        return null;
    }
}

// The shell around every page: the environment's edge, the top bar, the palette, the keyboard sheet, the toast, and
// what the pages share (the environment on screen, privacy mode).
export function App(props: { user: User; onLogout: () => void }): ReactNode {
    const path = usePath();
    const envsQuery = useQuery({ queryKey: ['envs'], queryFn: api.envs });
    // What fires now, every environment: the badge on Alerts, and the tab's title for a tab in the background.
    const firing = useQuery({ queryKey: ['firing'], queryFn: api.firing, refetchInterval: 30_000 }).data?.rules.length ?? 0;
    useEffect(() => {
        document.title = firing > 0 ? `(${firing}) Fleequid Logs` : 'Fleequid Logs';
    }, [firing]);
    const envs = useMemo(() => [...(envsQuery.data?.envs ?? [])].sort((a, b) => rank(a) - rank(b)), [envsQuery.data]);
    const [envChoice, setEnvChoice] = useState<string | null>(() => new URLSearchParams(location.search).get('env') ?? storedEnv());
    // Until the environments are known, none is on screen: the edge never flashes production's red by default.
    const env = envChoice && (envs.length === 0 || envs.includes(envChoice)) ? envChoice : (envs[0] ?? envChoice ?? '');
    const [privacy, setPrivacy] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const [palette, setPalette] = useState<Command[] | null>(null);
    const [shortcuts, setShortcuts] = useState(false);
    const [password, setPassword] = useState(false);
    // A new Explore (a line opened from elsewhere) is a fresh mount, its state read again from the URL.
    const [exploreNonce, setExploreNonce] = useState(0);
    const pageCommands = useRef<() => Command[]>(() => []);

    const say = useCallback((message: string) => setToast(message), []);
    useEffect(() => {
        if (!toast) {
            return;
        }
        const timer = setTimeout(() => setToast(null), 2600);

        return () => clearTimeout(timer);
    }, [toast]);

    const setEnv = useCallback((next: string) => {
        setEnvChoice(next);
        try {
            localStorage.setItem(ENV_KEY, next);
        } catch {
            // Remembered for this page only.
        }
    }, []);
    // Another environment from the top bar, the palette or E: the page keeps its query and range (they are in the URL),
    // an open line stays behind (it belongs to the environment it came from).
    const switchEnv = useCallback(
        (next: string) => {
            const params = new URLSearchParams(location.search);
            if (params.has('env') || params.has('line')) {
                params.set('env', next);
                params.delete('line');
                history.replaceState(null, '', `${location.pathname}?${params.toString()}`);
            }
            setEnv(next);
        },
        [setEnv],
    );
    const openLine = useCallback(
        (line: { env: string; id: string }) => {
            setEnv(line.env);
            navigate(`/?env=${line.env}&line=${line.id}`);
            setExploreNonce((nonce) => nonce + 1);
        },
        [setEnv],
    );
    const setPageCommands = useCallback((commands: () => Command[]) => {
        pageCommands.current = commands;
    }, []);

    const openPalette = useCallback(() => {
        const global: Command[] = [
            ...MODULES.map((module) => ({ icon: '→', label: `Go to ${module.label}`, run: () => navigate(module.path) })),
            ...(props.user.role === 'admin' ? [{ icon: '→', label: 'Go to the team', run: () => navigate('/team') }] : []),
            ...envs
                .filter((item) => item !== env)
                .map((item) => ({ icon: '◆', label: `Switch to ${envStyle(item).label.toLowerCase()}`, run: () => switchEnv(item) })),
            { icon: '▣', label: privacy ? 'Privacy mode off' : 'Privacy mode on', key: 'P', run: () => setPrivacy(!privacy) },
            { icon: '?', label: 'Keyboard shortcuts', key: '?', run: () => setShortcuts(true) },
            { icon: '⚿', label: 'Change my password', run: () => setPassword(true) },
            { icon: '⏻', label: 'Sign out', run: props.onLogout },
        ];
        setPalette([...pageCommands.current(), ...global]);
    }, [envs, env, privacy, props.user.role, props.onLogout, switchEnv]);

    // Keys that work on every page: ⌘K, ?, P, E, and G then a module's letter.
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                if (palette) {
                    setPalette(null);
                } else {
                    openPalette();
                }

                return;
            }
            const target = event.target as HTMLElement;
            if (
                palette ||
                shortcuts ||
                password ||
                ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) ||
                event.metaKey ||
                event.ctrlKey ||
                event.altKey
            ) {
                if (event.key === 'Escape') {
                    setShortcuts(false);
                    setPassword(false);
                }

                return;
            }
            const key = event.key === '/' && event.shiftKey ? '?' : event.key;
            if (goPending()) {
                clearGo();
                const module = MODULES.find((item) => item.key === key) ?? (key === 't' && props.user.role === 'admin' ? { path: '/team' } : null);
                if (module) {
                    event.preventDefault();
                    navigate(module.path);
                }

                return;
            }
            if (key === 'g') {
                markGo();
            } else if (key === '?') {
                event.preventDefault();
                setShortcuts(true);
            } else if (key === 'p') {
                setPrivacy((on) => !on);
            } else if (key === 'e' && envs.length > 1) {
                switchEnv(envs[(envs.indexOf(env) + 1) % envs.length] as string);
            }
        };
        window.addEventListener('keydown', onKey);

        return () => window.removeEventListener('keydown', onKey);
    }, [palette, shortcuts, password, openPalette, envs, env, switchEnv, props.user.role]);

    const shell = useMemo<Shell>(
        () => ({ user: props.user, env, envs, setEnv, privacy, setPrivacy, say, openLine, setPageCommands }),
        [props.user, env, envs, setEnv, privacy, say, openLine, setPageCommands],
    );

    const page = ((): ReactNode => {
        if (path === '/' || path === '/patterns') {
            return <Explore key={`${env}:${exploreNonce}`} mode={path === '/patterns' ? 'patterns' : 'lines'} />;
        }
        if (path === '/sources') {
            return <Sources />;
        }
        if (path === '/alerts') {
            return <Alerts />;
        }
        if (path === '/boards') {
            return <Boards key={env} />;
        }
        if (path === '/issues') {
            return <Issues key={env} />;
        }
        const issue = /^\/issues\/([0-9a-f]{16})$/.exec(path);
        if (issue) {
            return <IssuePage key={`${env}:${issue[1]}`} fingerprint={issue[1] as string} />;
        }
        if (path === '/team') {
            return props.user.role === 'admin' ? <Team /> : <Placeholder title="Team" text="Only an admin manages the team." />;
        }
        const module = MODULES.find((item) => item.path !== '/' && path.startsWith(item.path));

        return module ? (
            <Placeholder title={module.label} text="Being built right now." />
        ) : (
            <Placeholder title="Not here" text="There is no page at this address." />
        );
    })();

    return (
        <ShellContext.Provider value={shell}>
            <div className="shell" style={{ ['--env' as string]: env ? envStyle(env).color : 'var(--line-strong)' } as CSSProperties}>
                <div className="edge" />
                <TopBar
                    path={path}
                    env={env}
                    envs={envs}
                    onEnv={switchEnv}
                    privacy={privacy}
                    onPrivacy={setPrivacy}
                    user={props.user}
                    firing={firing}
                    onFind={openPalette}
                    onLogout={props.onLogout}
                    onShortcuts={() => setShortcuts(true)}
                    onPassword={() => setPassword(true)}
                />
                {page}
            </div>
            {palette && <Palette commands={palette} onClose={() => setPalette(null)} onFound={openLine} />}
            {shortcuts && <Shortcuts onClose={() => setShortcuts(false)} />}
            {password && (
                <PasswordDialog
                    onClose={() => setPassword(false)}
                    onDone={() => {
                        setPassword(false);
                        say('Password changed · your other sessions are signed out');
                    }}
                />
            )}
            {toast && <div className="toast">{toast}</div>}
        </ShellContext.Provider>
    );
}
